import React, { useEffect, useState, useCallback } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Checkbox,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Box,
	Typography,
	CircularProgress,
} from "@mui/material";
import { VendaService } from "../../service/venda_service";
import { ProdutoService } from "../../service/produto_service";

function ProductSelectionModal({ open, onClose, onSelect, unidadeDistribuidoraId, alreadySelected = [] }) {
	const [loading, setLoading] = useState(false);
	const [produtosDisponiveis, setProdutosDisponiveis] = useState([]);
	const [produtosFinais, setProdutosFinais] = useState([]);
	const [filtroNome, setFiltroNome] = useState("");
	const [selectedItems, setSelectedItems] = useState({});

	const loadProdutosFinais = useCallback(async () => {
		try {
			const produtoService = new ProdutoService();
			const produtos = await produtoService.getAll(unidadeDistribuidoraId, "final");
			const sortProdutos = (a, b) => {
				const nomeA = `${a.cultivo?.cultura || ""} ${a.cultivo?.variedade || ""}`.toLowerCase();
				const nomeB = `${b.cultivo?.cultura || ""} ${b.cultivo?.variedade || ""}`.toLowerCase();
				return nomeA.localeCompare(nomeB, "pt-BR");
			};
			setProdutosFinais((produtos || []).sort(sortProdutos));
		} catch (error) {
			console.error("Erro ao carregar produtos finais:", error);
		}
	}, [unidadeDistribuidoraId]);

	const loadProdutosDisponiveis = useCallback(async () => {
		if (!unidadeDistribuidoraId) return;

		setLoading(true);
		try {
			const vendaService = new VendaService();
			const produtos = await vendaService.getProdutosDisponiveis(
				unidadeDistribuidoraId,
				filtroNome || undefined
			);

			// Filtrar produtos já selecionados na venda
			const produtosFiltrados = produtos.filter(
				(p) => !alreadySelected.includes(p.id)
			);

			setProdutosDisponiveis(produtosFiltrados);
		} catch (error) {
			console.error("Erro ao carregar produtos disponíveis:", error);
			alert(error.message || "Erro ao carregar produtos disponíveis");
		} finally {
			setLoading(false);
		}
	}, [unidadeDistribuidoraId, filtroNome, alreadySelected]);

	useEffect(() => {
		if (open) {
			loadProdutosFinais();
			loadProdutosDisponiveis();
			setSelectedItems({});
		}
	}, [open, loadProdutosFinais, loadProdutosDisponiveis]);

	const handleToggleItem = (ordemProducaoId, produto) => {
		setSelectedItems((prev) => {
			const newSelected = { ...prev };
			if (newSelected[ordemProducaoId]) {
				// Desmarcar - remover o item
				delete newSelected[ordemProducaoId];
			} else {
				// Marcar - adicionar o item

				// Buscar se já existe algum item selecionado do mesmo produtoFinalId
				const produtoFinalId = produto.produtoFinal?.id;
				let precoExistente = 0;

				if (produtoFinalId) {
					// Procurar por outro item do mesmo tipo que já tenha preço definido
					const itemDoMesmoTipo = Object.values(newSelected).find(
						(item) => item.produtoFinalId === produtoFinalId && item.preco > 0
					);

					if (itemDoMesmoTipo) {
						precoExistente = itemDoMesmoTipo.preco;
					}
				}

				newSelected[ordemProducaoId] = {
					ordemProducaoId,
					quantidade: produto.estoqueDisponivel,
					volumes: produto.volumesDisponiveis,
					preco: precoExistente, // Usar preço existente ou 0
					produtoFinalId: produtoFinalId,
					ordemProducao: {
						id: produto.id,
						numeroOP: produto.numeroOP,
						data: produto.data,
						loteRastreabilidade: produto.loteRastreabilidade,
						produtoFinal: produto.produtoFinal,
						embalagemProdutoFinal: produto.embalagemProdutoFinal,
					},
				};
			}
			return newSelected;
		});
	};

	const handleQuantidadeChange = (ordemProducaoId, value, maxQuantidade) => {
		const quantidade = parseFloat(value) || 0;
		if (quantidade > maxQuantidade) {
			alert(`A quantidade não pode ser maior que ${maxQuantidade.toFixed(2)}`);
			return;
		}

		setSelectedItems((prev) => {
			const newSelected = { ...prev };
			if (newSelected[ordemProducaoId]) {
				newSelected[ordemProducaoId] = {
					...newSelected[ordemProducaoId],
					quantidade,
				};

				// Recalcular volumes com base na nova quantidade
				const produto = produtosDisponiveis.find((p) => p.id === ordemProducaoId);
				if (produto && produto.embalagemProdutoFinal) {
					const qtdPorVolume = produto.embalagemProdutoFinal.quantidade;
					if (qtdPorVolume > 0) {
						newSelected[ordemProducaoId].volumes = Math.floor(quantidade / qtdPorVolume);
					}
				}
			}
			return newSelected;
		});
	};

	const handlePrecoChange = (ordemProducaoId, value, produtoFinalId) => {
		const preco = parseFloat(value) || 0;

		setSelectedItems((prev) => {
			const newSelected = { ...prev };

			// Auto-preencher preço para TODOS os itens selecionados do mesmo produtoFinalId
			Object.keys(newSelected).forEach((key) => {
				const item = newSelected[key];

				// Comparar usando o produtoFinalId armazenado no item
				if (item.produtoFinalId === produtoFinalId) {
					newSelected[key] = {
						...item,
						preco,
					};
				}
			});

			return newSelected;
		});
	};

	const handleAddSelected = () => {
		const items = Object.values(selectedItems);

		// Validar que todos os itens têm quantidade e preço
		const invalid = items.find((item) => item.quantidade <= 0 || item.preco <= 0);
		if (invalid) {
			alert("Por favor, preencha quantidade e preço para todos os produtos selecionados");
			return;
		}

		onSelect(items);
		setSelectedItems({});
	};

	const handleCancel = () => {
		setSelectedItems({});
		onClose();
	};

	const formatDate = (dateString) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
	};

	const isSelected = (ordemProducaoId) => {
		return !!selectedItems[ordemProducaoId];
	};

	const selectedCount = Object.keys(selectedItems).length;

	return (
		<Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
			<DialogTitle>Selecionar Produtos</DialogTitle>
			<DialogContent>
				{/* Filtro */}
				<Box sx={{ mb: 3, mt: 2 }}>
					<FormControl fullWidth size="small">
						<InputLabel style={{ fontSize: "15px" }}>Filtrar por Produto</InputLabel>
						<Select
							value={filtroNome}
							onChange={(e) => setFiltroNome(e.target.value)}
							label="Filtrar por Produto"
							style={{ fontSize: "15px" }}
						>
							<MenuItem value="" style={{ fontSize: "15px" }}>
								Todos os produtos
							</MenuItem>
							{produtosFinais.map((produto) => {
								const cultura = produto.cultivo?.cultura || "Sem cultura";
								const variedade = produto.cultivo?.variedade
									? ` - ${produto.cultivo.variedade}`
									: "";
								return (
									<MenuItem key={produto.id} value={produto.id} style={{ fontSize: "15px" }}>
										{cultura}{variedade}
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
				</Box>

				{/* Tabela */}
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
						<CircularProgress sx={{ color: "#1a9e8f" }} />
					</Box>
				) : produtosDisponiveis.length === 0 ? (
					<Box sx={{ p: 4, textAlign: "center" }}>
						<Typography color="textSecondary">
							Nenhum produto disponível em estoque
						</Typography>
					</Box>
				) : (
					<TableContainer sx={{ maxHeight: 400 }}>
						<Table stickyHeader size="small">
							<TableHead>
								<TableRow>
									<TableCell padding="checkbox" sx={{ bgcolor: "#f5f5f5" }}>
										<Checkbox disabled />
									</TableCell>
									<TableCell sx={{ fontWeight: 600, fontSize: "14px", bgcolor: "#f5f5f5" }}>
										Data
									</TableCell>
									<TableCell sx={{ fontWeight: 600, fontSize: "14px", bgcolor: "#f5f5f5" }}>
										Produto Final
									</TableCell>
									<TableCell sx={{ fontWeight: 600, fontSize: "14px", bgcolor: "#f5f5f5" }}>
										Embalagem
									</TableCell>
									<TableCell sx={{ fontWeight: 600, fontSize: "14px", bgcolor: "#f5f5f5" }}>
										Estoque Disponível
									</TableCell>
									<TableCell sx={{ fontWeight: 600, fontSize: "14px", bgcolor: "#f5f5f5" }}>
										Volumes Disponíveis
									</TableCell>
									<TableCell sx={{ fontWeight: 600, fontSize: "14px", bgcolor: "#f5f5f5" }}>
										Lote Rastreabilidade
									</TableCell>
									<TableCell sx={{ fontWeight: 600, fontSize: "14px", bgcolor: "#f5f5f5", width: "120px" }}>
										Quantidade
									</TableCell>
									<TableCell sx={{ fontWeight: 600, fontSize: "14px", bgcolor: "#f5f5f5", width: "120px" }}>
										Preço (R$)
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{produtosDisponiveis.map((produto) => {
									const cultivo = produto.produtoFinal?.cultivo;
									const cultura = cultivo?.cultura || "-";
									const variedade = cultivo?.variedade ? ` - ${cultivo.variedade}` : "";
									const produtoDescricao = `${cultura}${variedade}`;

									const emb = produto.embalagemProdutoFinal;
									const embDescricao = emb
										? `${emb.descricao} (${emb.quantidade} ${emb.unidadeMedida?.sigla || ""})`
										: "-";

									const selected = isSelected(produto.id);
									const item = selectedItems[produto.id];

									return (
										<TableRow key={produto.id} hover>
											<TableCell padding="checkbox">
												<Checkbox
													checked={selected}
													onChange={() => handleToggleItem(produto.id, produto)}
												/>
											</TableCell>
											<TableCell sx={{ fontSize: "14px" }}>
												{formatDate(produto.data)}
											</TableCell>
											<TableCell sx={{ fontSize: "14px" }}>
												{produtoDescricao}
											</TableCell>
											<TableCell sx={{ fontSize: "14px" }}>
												{embDescricao}
											</TableCell>
											<TableCell sx={{ fontSize: "14px" }}>
												{produto.estoqueDisponivel.toLocaleString("pt-BR")}
											</TableCell>
											<TableCell sx={{ fontSize: "14px" }}>
												{produto.volumesDisponiveis}
											</TableCell>
											<TableCell sx={{ fontSize: "14px" }}>
												{produto.loteRastreabilidade || "-"}
											</TableCell>
											<TableCell>
												<TextField
													size="small"
													type="number"
													value={item?.quantidade || ""}
													onChange={(e) =>
														handleQuantidadeChange(
															produto.id,
															e.target.value,
															produto.estoqueDisponivel
														)
													}
													disabled={!selected}
													inputProps={{
														style: { fontSize: "14px" },
														min: 0,
														max: produto.estoqueDisponivel,
														step: 0.01,
													}}
													sx={{ width: "100%" }}
												/>
											</TableCell>
											<TableCell>
												<TextField
													size="small"
													type="number"
													value={item?.preco || ""}
													onChange={(e) =>
														handlePrecoChange(
															produto.id,
															e.target.value,
															produto.produtoFinal?.id
														)
													}
													disabled={!selected}
													inputProps={{
														style: { fontSize: "14px" },
														min: 0,
														step: 0.01,
													}}
													sx={{ width: "100%" }}
												/>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{/* Contador de selecionados */}
				{selectedCount > 0 && (
					<Box sx={{ mt: 2 }}>
						<Typography variant="body2" color="primary">
							{selectedCount} produto(s) selecionado(s)
						</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel} sx={{ color: "#666" }}>
					Cancelar
				</Button>
				<Button
					onClick={handleAddSelected}
					variant="contained"
					disabled={selectedCount === 0}
					sx={{
						bgcolor: "#1a9e8f",
						"&:hover": {
							bgcolor: "#138a7d",
						},
					}}
				>
					Adicionar Selecionados ({selectedCount})
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default ProductSelectionModal;
